import { DateTime } from 'luxon';
import {
  clampAnchorToEventListBounds,
  getEventListBounds,
} from '@/features/events/lib/eventListWindow.lib';
import type { DateTimeRange } from '@/features/events/lib/range.lib';
import { getDisplayTimezone } from '@/features/experiences/lib/experience';
import type { PageExperience } from '@/features/experiences/types';
import { startOfWeekFor } from '@/utils/dates';
import { resolveWeekStartDay } from '../../weekStart.lib';

// Viewer-side week navigation: which weeks are reachable, and the range a given
// anchor date resolves to. Shared by every week-shaped collection block so the
// selectable range and the grouping agree on where a week starts.

export const getWeekRangeBounds = (
  experience: PageExperience
): { start: DateTime; end: DateTime } =>
  getEventListBounds(experience, getDisplayTimezone(experience));

const clampAnchor = (anchor: DateTime, experience: PageExperience) =>
  clampAnchorToEventListBounds(
    anchor,
    experience,
    getDisplayTimezone(experience)
  );

export const getWeekRangeForAnchor = (
  anchor: DateTime,
  experience?: PageExperience
): DateTimeRange => {
  const clamped = experience ? clampAnchor(anchor, experience) : anchor;
  // With an experience, resolve its per-view/per-layout week start; without one
  // (add-import bound), fall back to Sunday.
  const weekStart = startOfWeekFor(
    clamped,
    experience ? resolveWeekStartDay(experience) : 0
  );
  return {
    startDate: weekStart,
    endDate: weekStart.plus({ days: 6 }).endOf('day'),
  };
};

/**
 * The week to show when no range is selected: the one containing today, or the
 * nearest selectable week when today falls outside the experience's window.
 * getWeekRangeForAnchor does the clamping, so today only has to be handed to it.
 */
export const getDefaultWeekRange = (
  experience: PageExperience
): DateTimeRange =>
  getWeekRangeForAnchor(
    DateTime.local({ zone: getDisplayTimezone(experience) }),
    experience
  );
